import React from 'react';
import { ArrowRight, Check, Clock, Users, Lightbulb, ChevronRight } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function CoachingSkillsForManagers() {
  const {
    language
  } = useLanguage();
  const en = {
    badge: 'Leadership development',
    title: 'Coaching Skills',
    titleHighlight: 'for Managers',
    intro1: 'Most managers know they should ask more questions and give less advice. Most managers also find that in practice, the advice keeps coming out — faster and more confidently than intended.',
    intro2: 'This is Michael Bungay Stanier\'s "advice monster" — the deeply conditioned habit of moving straight to solutions rather than staying curious long enough to understand the actual problem. Breaking it requires deliberate practice, not just awareness.',
    heroCta: 'Get in touch',
    notTitle: 'This is not a coaching certification',
    notDesc: 'Managers cannot be professional coaches for their own people — the power dynamic makes that impossible, and pretending otherwise causes harm. This programme is about developing a coaching approach to leadership: staying curious longer, asking better questions, and creating the conditions where people can solve their own problems.',
    whatTitle: 'What the programme develops',
    whatItems: ['The seven questions from The Coaching Habit — when and how to use them', 'Recognising and interrupting the advice monster reflex', 'The difference between coaching mode, mentoring mode and managing mode — and when to use each', 'Clean questions: how to ask without leading or contaminating the answer', 'What psychological safety actually requires from a leader', 'Practical tools for 1:1s that develop rather than just report'],
    formatTitle: 'Format',
    formatDesc: 'Six sessions of two hours each, delivered online over six to eight weeks. Sessions alternate between concept introduction and practice — each session includes real scenarios from participants\' own contexts. The gap between sessions is deliberate: participants try something, notice what happens, and bring that back.',
    formatMeta: '6 x 2 hours online · 6-8 weeks · maximum 8 participants',
    articleNote: 'The thinking behind this programme is developed across the Coaching Leadership series on Substack.',
    ctaTitle: "Let's talk",
    ctaDesc: 'This programme works best in small groups where participants share similar contexts. Tell me about your situation.',
    ctaCta: 'Get in touch'
  };
  const is = {
    badge: 'Leiðtogaþróun',
    title: 'Þjálfunartækni',
    titleHighlight: 'fyrir stjórnendur',
    intro1: 'Flestir stjórnendur vita að þeir ættu að spyrja fleiri spurninga og gefa færri ráð. Flestir stjórnendur finna líka að í reynd heldur ráðgjöfin áfram að koma út.',
    intro2: 'Þetta er "advice monster" Michael Bungay Stanier - djúpt innrætt venja að fara beint í lausnir frekar en að vera forvitinn nógu lengi til að skilja raunverulega vandann.',
    heroCta: 'Hafa samband',
    notTitle: 'Þetta er ekki þjálfunarvottun',
    notDesc: 'Stjórnendur geta ekki verið faglegir þjálfar fyrir eigið fólk - valdauppbygging gerir það ómögulegt. Þetta námskeið fjallar um að þróa þjálfunarnálgun við leiðtogastarf: vera forvitinn lengur, spyrja betri spurninga.',
    whatTitle: 'Hvað námskeiðið þróar',
    whatItems: ['Sjö spurningarnar úr The Coaching Habit - hvenær og hvernig á að nota þær', 'Að þekkja og rjúfa advice monster viðbragðið', 'Munurinn á þjálfunarham, leiðbeiningaham og stjórnunarham', 'Hreinar spurningar: hvernig á að spyrja án þess að leiða eða menga svarið', 'Hvað sálfræðilegt öryggi krefst raunverulega af leiðtoga', 'Hagnýt verkfæri fyrir 1:1 fundi sem þróa frekar en bara skila'],
    formatTitle: 'Snið',
    formatDesc: 'Sex lotur tvær klukkustundir hver, afhent á netinu á sex til átta vikum. Lotúrnar skiptast á milli hugmyndakynninga og þjálfunar.',
    formatMeta: '6 x 2 klukkustundir á netinu · 6-8 vikur · hámark 8 þátttakendur',
    articleNote: 'Hugsuningin bak við þetta námskeið er þróuð í Coaching Leadership röðinni á Substack.',
    ctaTitle: 'Við skulum tala saman',
    ctaDesc: 'Þetta námskeið virkar best í litlum hópum þar sem þátttakendur deila svipuðu samhengi.',
    ctaCta: 'Hafa samband'
  };
  const c = language === 'is' ? is : en;
  return <PageTransition data-id="element-1887">
      <div className="pt-12 pb-24" data-id="element-1888">
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20 text-center" data-id="element-1889">
          <Animate data-id="element-1890">
            <span className="inline-block px-4 py-2 bg-teal/10 text-teal font-medium rounded-full text-sm mb-6" data-id="element-1891">{c.badge}</span>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-id="element-1892">{c.title} <span className="text-teal" data-id="element-1893">{c.titleHighlight}</span></h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-4" data-id="element-1894">{c.intro1}</p>
            <p className="text-lg text-gray-500 leading-relaxed mb-8" data-id="element-1895">{c.intro2}</p>
            <Link to="/contact" data-id="element-1896"><Button rightIcon={<ArrowRight size={18} data-id="element-1898" />} data-id="element-1897">{c.heroCta}</Button></Link>
          </Animate>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-16" data-id="element-1899">
          <Animate data-id="element-1900">
            <Card className="p-8 border-2 border-teal/20 bg-teal/5" data-id="element-1901">
              <div className="flex items-start gap-4" data-id="element-1902">
                <div className="p-2 bg-teal/10 rounded-xl flex-shrink-0" data-id="element-1903"><Lightbulb size={24} className="text-teal" data-id="element-1904" /></div>
                <div data-id="element-1905">
                  <h2 className="font-bold text-charcoal text-lg mb-2" data-id="element-1906">{c.notTitle}</h2>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1907">{c.notDesc}</p>
                </div>
              </div>
            </Card>
          </Animate>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-16" data-id="element-1908">
          <Animate className="mb-6" data-id="element-1909"><h2 className="text-2xl font-bold text-charcoal" data-id="element-1910">{c.whatTitle}</h2></Animate>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1911">
            {c.whatItems.map((item, i) => <Animate key={i} delay={i * 0.05} data-id="element-1912">
                <div className="flex items-start gap-3" data-id="element-1913">
                  <div className="mt-1 p-1 rounded-full bg-teal/10 text-teal flex-shrink-0" data-id="element-1914"><Check size={12} strokeWidth={3} data-id="element-1915" /></div>
                  <span className="text-gray-700 leading-relaxed" data-id="element-1916">{item}</span>
                </div>
              </Animate>)}
          </div>
        </section>

        <section className="bg-cream py-16 mb-16" data-id="element-1917">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1918">
            <Animate data-id="element-1919">
              <h2 className="text-2xl font-bold text-charcoal mb-4" data-id="element-1920">{c.formatTitle}</h2>
              <p className="text-gray-600 leading-relaxed mb-6" data-id="element-1921">{c.formatDesc}</p>
              <div className="flex flex-wrap gap-4" data-id="element-1922">
                {c.formatMeta.split(' · ').map((m, i) => <div key={i} className="flex items-center gap-2 bg-white rounded-xl px-4 py-2 shadow-sm" data-id="element-1923">
                    {i === 0 && <Clock size={16} className="text-teal" data-id="element-1924" />}
                    {i === 1 && <Clock size={16} className="text-teal" data-id="element-1925" />}
                    {i === 2 && <Users size={16} className="text-teal" data-id="element-1926" />}
                    <span className="text-sm font-medium text-charcoal" data-id="element-1927">{m}</span>
                  </div>)}
              </div>
            </Animate>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-16" data-id="element-1928">
          <Animate data-id="element-1929">
            <Card className="p-6 border-l-4 border-teal bg-teal/5" data-id="element-1930">
              <div className="flex items-start gap-3" data-id="element-1931">
                <Lightbulb size={20} className="text-teal flex-shrink-0 mt-0.5" data-id="element-1932" />
                <p className="text-gray-700" data-id="element-1933">{c.articleNote}{' '}
                  <a href="https://helgig.substack.com" target="_blank" rel="noopener noreferrer" className="text-teal font-semibold hover:underline" data-id="element-1934">Substack <ChevronRight size={14} className="inline" data-id="element-1935" /></a>
                </p>
              </div>
            </Card>
          </Animate>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1936">
          <Animate data-id="element-1937">
            <Card className="p-10 bg-gradient-to-br from-teal/5 to-coral/5 border-0 text-center" data-id="element-1938">
              <h2 className="text-2xl font-bold mb-3" data-id="element-1939">{c.ctaTitle}</h2>
              <p className="text-gray-500 mb-6 max-w-lg mx-auto" data-id="element-1940">{c.ctaDesc}</p>
              <Link to="/contact" data-id="element-1941"><Button variant="primary" rightIcon={<ArrowRight size={18} data-id="element-1943" />} data-id="element-1942">{c.ctaCta}</Button></Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}